import { RefObject, useEffect, useState } from "react";

import { AdaptiveContainerAttributesType } from "types";
import { ResponsiveBreakPoints } from "consts";

type useAdaptiveContainerAttributesProps = {
  containerRef?: RefObject<HTMLElement> | null | undefined;
};

type useAdaptiveContainerAttributesReturn = AdaptiveContainerAttributesType & {
  container?: HTMLElement | null;
};

const getAdaptiveContainerState = (containerWidth = 0) => {
  return {
    isContainerMobileSm: containerWidth < ResponsiveBreakPoints.MobileSm[1],
    isContainerMobile: containerWidth < ResponsiveBreakPoints.Mobile[1],
    isContainerTabletSm:
      containerWidth > ResponsiveBreakPoints.TabletSm[0] && containerWidth < ResponsiveBreakPoints.TabletSm[1],
    isContainerTabletLg:
      containerWidth > ResponsiveBreakPoints.TabletLg[0] && containerWidth < ResponsiveBreakPoints.TabletLg[1],
    isContainerTablet:
      containerWidth > ResponsiveBreakPoints.Tablet[0] && containerWidth < ResponsiveBreakPoints.Tablet[1],
    isContainerNotebook:
      containerWidth > ResponsiveBreakPoints.Notebook[0] && containerWidth < ResponsiveBreakPoints.Notebook[1],
    isContainerNotebookLg:
      containerWidth > ResponsiveBreakPoints.NotebookLg[0] && containerWidth < ResponsiveBreakPoints.NotebookLg[1],
    isContainerDesktop:
      containerWidth > ResponsiveBreakPoints.Desktop[0] && containerWidth < ResponsiveBreakPoints.Desktop[1],
    isContainerTV: containerWidth > ResponsiveBreakPoints.TV[0],
  };
};

const useAdaptiveContainerAttributes = ({
  containerRef = null,
}: useAdaptiveContainerAttributesProps): useAdaptiveContainerAttributesReturn => {
  const [containerWidth, setContainerWidth] = useState(containerRef?.current?.clientWidth || 0);
  const [container, setContainer] = useState(containerRef?.current || null);
  const [adaptiveAttributes, setAdaptiveAttributes] = useState(
    getAdaptiveContainerState(containerRef?.current?.clientWidth || 0),
  );

  const updateContainerAttributes = (newContainerWidth = 0) => {
    setContainerWidth(newContainerWidth);
    setAdaptiveAttributes(getAdaptiveContainerState(newContainerWidth));
  };

  useEffect(() => {
    containerRef?.current && setContainer(containerRef.current);
  }, [containerRef]);

  useEffect(() => {
    const listenerType = "resize";
    const onResize = () => updateContainerAttributes(container?.clientWidth || 0);

    if (container && container.clientWidth) {
      updateContainerAttributes(container?.clientWidth || 0);
      window.addEventListener(listenerType, onResize, { passive: true });
    } else {
      window.removeEventListener(listenerType, onResize);
    }

    return () => window.removeEventListener(listenerType, onResize);
  }, [container]);

  return {
    container,
    containerWidth,
    ...adaptiveAttributes,
  };
};

export default useAdaptiveContainerAttributes;
